import * as React from "react";
import { useWebSocket, ReadyState } from "@/lib/cjs-interop";
import { SnapshotSearchEvent } from "./types";
import { BusinessReportOutcome } from "@/modules/snapshots/businessReports.types";

interface Props {
  searchId: string;
  setThinking: (thinking: string) => void;
  setDocuments: React.Dispatch<React.SetStateAction<number>>;
  setIsProcessing: (isProcessing: boolean) => void;
  setSnapshotCollectionOutcomes: React.Dispatch<React.SetStateAction<BusinessReportOutcome[] | undefined>>;
  setIsThinkingComplete: (isComplete: boolean) => void;
  setRelevantFields?: (fields: string[]) => void;
}

export const GridSidebarSearchWebsocket: React.FC<Props> = ({
  setDocuments,
  searchId,
  setThinking,
  setSnapshotCollectionOutcomes,
  setIsProcessing,
  setIsThinkingComplete,
  setRelevantFields,
}) => {
  const { lastJsonMessage, readyState, sendJsonMessage } = useWebSocket<SnapshotSearchEvent>(
    "/pf-mainweb/business-reports/ws",
    {
      shouldReconnect: event => event?.code !== 1000,
      reconnectInterval: 3000,
      skipAssert: true,
    },
  );

  React.useEffect(() => {
    if (readyState === ReadyState.OPEN && searchId) {
      sendJsonMessage({ query_id: searchId });
    }
  }, [readyState, searchId, sendJsonMessage]);

  React.useEffect(() => {
    if (!lastJsonMessage) return;
    if (lastJsonMessage.event === "relevant_fields") {
      const context = JSON.parse(lastJsonMessage.context);
      setThinking(context.reasoning);
      // Extract field names if available
      if (context.field_names && Array.isArray(context.field_names)) {
        setRelevantFields?.(context.field_names);
      }
    } else if (lastJsonMessage.event === "documents") {
      const documents: number = JSON.parse(lastJsonMessage.context);
      setDocuments(prev => prev + documents);
      // Mark thinking as complete when documents start arriving
      setIsThinkingComplete(true);
    } else if (lastJsonMessage.event === "search_response") {
      setSnapshotCollectionOutcomes(prev => {
        const existing = prev || [];
        const newResults = JSON.parse(lastJsonMessage.context).results;
        const existingIds = new Set(existing.map((item: BusinessReportOutcome) => item.id));
        const uniqueNewResults = newResults.filter((item: BusinessReportOutcome) => !existingIds.has(item.id));
        return [...existing, ...uniqueNewResults];
      });
    } else if (lastJsonMessage.event === "search_response_complete") {
      setIsProcessing(false);
      // Ensure thinking is marked complete when search completes
      setIsThinkingComplete(true);
    }
  }, [
    lastJsonMessage,
    setDocuments,
    setIsProcessing,
    setSnapshotCollectionOutcomes,
    setThinking,
    setIsThinkingComplete,
    setRelevantFields,
  ]);

  return null;
};
